/*
 * Copyright (c) Harness Inc.
 *
 * This source code is licensed under the Apache 2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react'
import css from './Carousel.css'
import cx from 'classnames'
import { Layout } from '../../layouts/Layout'
import { Icon } from '@harnessio/icons'

export interface CarouselProps {
  className?: string
  defaultSlide?: number // 1
  nextElement?: JSX.Element
  previousElement?: JSX.Element
  hideIndicators?: boolean // false
  onChange?: (slideNumber: number) => void
  children: Array<React.ReactElement<HTMLElement>> | React.ReactElement<HTMLElement>
  indicatorsClassName?: string
  activeIndicatorClassName?: string
  slideClassName?: string
  hideSlideChangeButtons?: boolean

  /** When true, the slide will change automatically  */
  autoPlay?: boolean

  /** Interval in milliseconds to automatically go to the next item when autoPlay is true, defaults to 2000  */
  autoPlayInterval?: number
}

export const Carousel: React.FC<CarouselProps> = ({
  className = '',
  children,
  defaultSlide = 1,
  nextElement,
  previousElement,
  hideIndicators = false,
  autoPlay,
  autoPlayInterval = 2000,
  onChange,
  hideSlideChangeButtons = false,
  indicatorsClassName,
  activeIndicatorClassName = '',
  slideClassName
}) => {
  const [totalSlides, setTotalSlides] = React.useState(1)
  const [activeSlide, setActiveSlide] = React.useState(1)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  function resetTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  React.useEffect(() => {
    let slides = 1
    if (Array.isArray(children)) {
      slides = children.length
    }
    setTotalSlides(slides)
  }, [children])

  React.useEffect(() => {
    setActiveSlide(defaultSlide)
  }, [defaultSlide])

  React.useEffect(() => {
    resetTimeout()

    if (autoPlay && Array.isArray(children) && children.length > activeSlide) {
      timeoutRef.current = setTimeout(() => {
        setActiveSlide(activeSlide + 1)
      }, autoPlayInterval)
    }

    onChange?.(activeSlide)

    return () => {
      resetTimeout()
    }
  }, [activeSlide])

  const indicators = [...Array(totalSlides)].map((_ar, index) => (
    <span
      key={index}
      className={cx(css.indicator, {
        [css.active]: activeSlide === index + 1,
        [activeIndicatorClassName]: activeSlide === index + 1 && activeIndicatorClassName
      })}
      onClick={() => setActiveSlide(index + 1)}
    />
  ))

  const items = [...Array(totalSlides)].map((_ar, index) => (
    <div
      key={index}
      className={cx(
        css.carouselItem,
        { [css.carouselItemPrev]: activeSlide === index + 2 },
        { [css.active]: activeSlide === index + 1 },
        { [css.carouselItemNext]: activeSlide === index },
        slideClassName
      )}>
      {Array.isArray(children) ? children[index] : children}
    </div>
  ))

  return (
    <Layout.Vertical spacing="xsmall" padding="small" className={cx(css.carousel, className)}>
      <div className={css.carouselView}>
        {!previousElement ? (
          activeSlide > 1 &&
          !hideSlideChangeButtons && (
            <div className={css.carouselLeft} onClick={() => setActiveSlide(activeSlide - 1)}>
              <div>
                <Icon name="main-chevron-left" size={20} color="white" />
              </div>
            </div>
          )
        ) : (
          <span onClick={() => setActiveSlide(activeSlide - 1)}>{previousElement} </span>
        )}
        {items}
        {!nextElement ? (
          activeSlide < totalSlides &&
          !hideSlideChangeButtons && (
            <div className={css.carouselRight} onClick={() => setActiveSlide(activeSlide + 1)}>
              <div>
                <Icon name="main-chevron-right" size={20} color="white" />
              </div>
            </div>
          )
        ) : (
          <span onClick={() => setActiveSlide(activeSlide + 1)}>{nextElement} </span>
        )}
      </div>
      {!hideIndicators && <div className={cx(css.carouselIndicators, indicatorsClassName)}>{indicators}</div>}
    </Layout.Vertical>
  )
}
